import { FC, useState, MouseEvent } from 'react'
import { IconButton, MenuItem, Typography } from '@mui/material'
import { ProductOptionsMenuContainer, Menu } from './productOptionsMenu.styles'
import { SSMCellProduct } from 'models'

interface MenuOption {
  label: string
  action: () => void
  icon: JSX.Element
}

interface Props {
  product: SSMCellProduct
  handleEditProduct: (product: SSMCellProduct) => void
  handleOpenVendorPage: (product: SSMCellProduct) => void
  handleOpenProductPage: (product: SSMCellProduct) => void
  handleRemoveFromCell: (product: SSMCellProduct) => void
  handleRemoveFromSSM: (product: SSMCellProduct) => void
}

const ProductOptoinsMenu: FC<Props> = ({
  product,
  handleEditProduct,
  handleOpenVendorPage,
  handleOpenProductPage,
  handleRemoveFromCell,
  handleRemoveFromSSM
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const options = [
    {
      label: 'Edit',
      icon: EditIcon,
      action: (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        handleClose()
        handleEditProduct(product)
      }
    },
    {
      label: 'Vendor page',
      icon: ProductPageIcon,
      action: (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        handleClose()
        handleOpenVendorPage(product)
      }
    },
    {
      label: 'Product page',
      icon: ProductPageIcon,
      action: (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        handleClose()
        handleOpenProductPage(product)
      }
    },
    {
      label: 'Remove from this area only',
      icon: RemoveFromAreaIcon,
      action: (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        handleClose()
        handleRemoveFromCell(product)
      }
    },
    {
      label: 'Remove from SSM',
      icon: RemoveFromSSMIcon,
      action: (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        handleClose()
        handleRemoveFromSSM(product)
      }
    }
    // A vendor row has no product page of its own; the entry would only reopen the vendor url.
  ].filter((option) => option.label !== 'Product page' || product.is_product) as MenuOption[]

  return (
    <ProductOptionsMenuContainer>
      <IconButton
        className="menu-icon-button"
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreIcon />
      </IconButton>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.label} selected={option.label === 'Pyxis'} onClick={option.action}>
            {option.icon}
            <Typography>{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </ProductOptionsMenuContainer>
  )
}

export default ProductOptoinsMenu

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7 4C7 3.44772 7.44772 3 8 3C8.55228 3 9 3.44772 9 4C9 4.55228 8.55228 5 8 5C7.44772 5 7 4.55228 7 4ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8C9 8.55228 8.55228 9 8 9C7.44772 9 7 8.55228 7 8ZM7 12C7 11.4477 7.44772 11 8 11C8.55228 11 9 11.4477 9 12C9 12.5523 8.55228 13 8 13C7.44772 13 7 12.5523 7 12Z"
      fill="#D0C3CC"
    />
  </svg>
)

const RemoveFromSSMIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.82692 6L9.59615 12M6.40385 12L6.17308 6M12.8184 3.86038C13.0464 3.89481 13.2736 3.93165 13.5 3.97086M12.8184 3.86038L12.1065 13.115C12.0464 13.8965 11.3948 14.5 10.611 14.5H5.38905C4.60524 14.5 3.95358 13.8965 3.89346 13.115L3.18157 3.86038M12.8184 3.86038C12.0542 3.74496 11.281 3.65657 10.5 3.59622M2.5 3.97086C2.72638 3.93165 2.95358 3.89481 3.18157 3.86038M3.18157 3.86038C3.94585 3.74496 4.719 3.65657 5.5 3.59622M10.5 3.59622V2.98546C10.5 2.19922 9.8929 1.54282 9.10706 1.51768C8.73948 1.50592 8.37043 1.5 8 1.5C7.62957 1.5 7.26052 1.50592 6.89294 1.51768C6.1071 1.54282 5.5 2.19922 5.5 2.98546V3.59622M10.5 3.59622C9.67504 3.53247 8.84131 3.5 8 3.5C7.15869 3.5 6.32496 3.53247 5.5 3.59622"
      stroke="#E74C3C"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const RemoveFromAreaIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.5 6.5L9.5 9.5M9.5 6.5L6.5 9.5M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
      stroke="#E74C3C"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ProductPageIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 4H3.5C2.67157 4 2 4.67157 2 5.5V12.5C2 13.3284 2.67157 14 3.5 14H10.5C11.3284 14 12 13.3284 12 12.5V7M5 11L14 2M14 2L10.5 2M14 2V5.5"
      stroke="#D0C3CC"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const EditIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.2411 2.99111L12.3661 1.86612C12.8543 1.37796 13.6457 1.37796 14.1339 1.86612C14.622 2.35427 14.622 3.14573 14.1339 3.63388L4.55479 13.213C4.20234 13.5654 3.76762 13.8245 3.28993 13.9668L1.5 14.5L2.03319 12.7101C2.17548 12.2324 2.43456 11.7977 2.78701 11.4452L11.2411 2.99111ZM11.2411 2.99111L13 4.74999"
      stroke="#D0C3CC"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
